package com.evoapps.evoshift.domain.rosterimport

/**
 * A shift's clock times as a spreadsheet writes them: [start] and [end] as "HH:MM" (24-hour). An
 * [end] at or before [start] is the next day ("22:00"–"06:00" runs over midnight).
 */
data class ClockRange(val start: String, val end: String) {
    /** Over midnight. */
    val overnight: Boolean get() = end <= start

    /** "8:00–16:00" style, for the preview (hours without a leading zero). */
    val label: String get() = "${short(start)}–${short(end)}"

    private fun short(time: String) = time.removePrefix("0").let { if (it.startsWith(":")) "0$it" else it }
}

/**
 * SH-217: reads shift times typed in a roster spreadsheet. What people type: "8-16", "8:00-16:00",
 * "8.00–16.00", "7:30 - 15:30", "07:30-15:30", "22-6" (over midnight), "6-24" (to midnight), with
 * a hyphen, an en or em dash, a minus sign or "do"/"to" between, and spaces anywhere around.
 * Several shifts in one cell ("8-12 / 16-20", "8-12; 16-20", one per line) are read when every part
 * is a time range. Anything else is not a time ([parseRanges] returns null) and goes to the code
 * dictionary.
 */
object ShiftTimeParser {

    private val DASHES = charArrayOf('-', '–', '—', '‐', '‑', '−', '~')
    private val SEPARATORS = charArrayOf('/', ';', ',', '\n', '|', '+', '&')

    /** The cell's shifts, or null when the cell is not (only) time ranges. A blank cell is null too. */
    fun parseRanges(cell: String): List<ClockRange>? {
        val text = cell.trim()
        if (text.isEmpty()) return null
        parseRange(text)?.let { return listOf(it) }
        // "8-12, 16-20" and "8-12 / 16-20"; one separator at a time, so "8,00-12,00 / 16,00-20,00" works.
        for (separator in SEPARATORS) {
            val parts = text.split(separator).map { it.trim() }.filter { it.isNotEmpty() }
            if (parts.size < 2) continue
            val ranges = parts.map { parseRange(it) }
            if (ranges.all { it != null }) return ranges.filterNotNull()
        }
        return null
    }

    /** One range ("8-16", "7:30 - 15:30", "22.00–6.00", "8 do 16"); null when [text] is not one. */
    fun parseRange(text: String): ClockRange? {
        val compact = text.trim().lowercase()
        if (compact.isEmpty()) return null
        val (left, right) = splitRange(compact) ?: return null
        val start = parseClock(left) ?: return null
        var end = parseClock(right, allowMidnightEnd = true) ?: return null
        if (end == "24:00") end = "00:00"
        if (start == end) return null
        return ClockRange(start, end)
    }

    /** The two sides of a range: around its one dash, or around "do" / "to" / "до". */
    private fun splitRange(text: String): Pair<String, String>? {
        val dashAt = text.indexOfAny(DASHES)
        if (dashAt >= 0) {
            if (text.indexOfAny(DASHES, dashAt + 1) >= 0) return null
            return text.substring(0, dashAt).trim() to text.substring(dashAt + 1).trim()
        }
        for (word in listOf(" do ", " to ", " до ")) {
            val at = text.indexOf(word)
            if (at > 0) return text.substring(0, at).trim() to text.substring(at + word.length).trim()
        }
        return null
    }

    /**
     * A clock time: "8", "08", "8:00", "8.00", "8,00", "8:5" is not one (minutes take two digits),
     * "800"/"0800" (four digits as on some sheets), "8h", "8:00h"; hours 0–23 ([allowMidnightEnd]: 24
     * too, only as "24" or "24:00"). Returns "HH:MM" or null.
     */
    fun parseClock(text: String, allowMidnightEnd: Boolean = false): String? {
        var t = text.trim().lowercase().removeSuffix("h").removeSuffix("godz.").removeSuffix("godz").trim()
        if (t.isEmpty() || t.length > 5) return null
        val sep = t.indexOfFirst { it == ':' || it == '.' || it == ',' }
        val hours: Int
        val minutes: Int
        if (sep >= 0) {
            val h = t.substring(0, sep)
            val m = t.substring(sep + 1)
            if (h.isEmpty() || h.length > 2 || m.length != 2 || !h.all(Char::isDigit) || !m.all(Char::isDigit)) return null
            hours = h.toInt()
            minutes = m.toInt()
        } else {
            if (!t.all(Char::isDigit)) return null
            when (t.length) {
                1, 2 -> { hours = t.toInt(); minutes = 0 }
                3, 4 -> { t = t.padStart(4, '0'); hours = t.take(2).toInt(); minutes = t.takeLast(2).toInt() }
                else -> return null
            }
        }
        if (minutes > 59) return null
        if (hours == 24 && minutes == 0 && allowMidnightEnd) return "24:00"
        if (hours > 23) return null
        return "${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}"
    }
}
